import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Contact, Pencil, Mail, Phone, MapPin, FileText, Building2,
  ShieldCheck, BadgeCheck, X, Bike, Calendar, Loader2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CustomerForm } from "@/pages/Customers";
import { toast } from "sonner";

const DOC_LABEL = { carta_identita: "Carta d'identità", patente: "Patente", passaporto: "Passaporto" };

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch (e) { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch (e) { return iso; }
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="h-4 w-4 text-zinc-600 mt-0.5 shrink-0" />
      <div className="flex-1 flex items-center justify-between gap-3">
        <span className="text-zinc-500">{label}</span>
        <span className="text-white font-medium text-right">{value || "—"}</span>
      </div>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [cust, setCust] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const c = await api.get(`/customers/${id}`);
      setCust(c.data);
      try { const r = await api.get(`/customers/${id}/rentals`); setRentals(r.data); } catch (e) {}
    } catch (e) {
      toast.error("Cliente non trovato");
      navigate("/clienti");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openEdit = () => { setForm({ ...cust }); setOpen(true); };
  const save = async () => {
    if (!form.consenso_privacy) { toast.error("Il consenso privacy è obbligatorio"); return; }
    setSaving(true);
    try { await api.put(`/customers/${id}`, form); toast.success("Cliente aggiornato"); setOpen(false); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#f98125]" /></div>;
  if (!cust) return null;

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <button onClick={() => navigate("/clienti")} data-testid="back-customers" className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-5"><ArrowLeft className="h-4 w-4" /> Clienti</button>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 bg-[#f98125]/15 rounded-sm flex items-center justify-center text-[#f98125] font-display text-3xl uppercase">
            {(cust.nome?.[0] || "") + (cust.cognome?.[0] || "")}
          </div>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none">{cust.nome} {cust.cognome}</h1>
            <div className="text-zinc-500 text-sm mt-1">{cust.ragione_sociale || "Cliente privato"}</div>
          </div>
        </div>
        {hasPerm("customers.edit") && (
          <button onClick={openEdit} data-testid="edit-customer-detail"
            className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors active:scale-95">
            <Pencil className="h-5 w-5" /> Modifica
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1 space-y-5">
          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Contatti</div>
            <Row icon={Mail} label="Email" value={cust.email} />
            <Row icon={Phone} label="Telefono" value={cust.telefono} />
            <Row icon={MapPin} label="Indirizzo" value={[cust.indirizzo, cust.cap, cust.citta].filter(Boolean).join(", ")} />
          </div>

          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Documento</div>
            <Row icon={FileText} label="Tipo" value={DOC_LABEL[cust.tipo_documento] || cust.tipo_documento} />
            <Row icon={FileText} label="Numero" value={cust.numero_documento} />
          </div>

          {(cust.ragione_sociale || cust.piva || cust.codice_fiscale) && (
            <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Dati amministrativi (export)</div>
              <Row icon={Building2} label="Ragione sociale" value={cust.ragione_sociale} />
              <Row icon={FileText} label="P.IVA" value={cust.piva} />
              <Row icon={FileText} label="Codice Fiscale" value={cust.codice_fiscale} />
            </div>
          )}

          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Consensi</div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-zinc-400"><ShieldCheck className="h-4 w-4" /> Privacy</span>
              {cust.consenso_privacy
                ? <span className="text-[#39FF14] text-xs">✓ {fmtDateTime(cust.consenso_privacy_at)}</span>
                : <span className="text-[#FF3B30] text-xs">Non concesso</span>}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-zinc-400"><BadgeCheck className="h-4 w-4" /> Marketing</span>
              {cust.consenso_marketing
                ? <span className="text-[#39FF14] text-xs">✓ {fmtDateTime(cust.consenso_marketing_at)}</span>
                : <span className="text-zinc-600 text-xs">No</span>}
            </div>
          </div>

          {cust.note && (
            <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Note interne</div>
              <p className="text-sm text-zinc-300">{cust.note}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="bg-[#141414] border border-zinc-800 rounded-sm p-5">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold flex items-center gap-2"><Bike className="h-5 w-5 text-[#f98125]" /> Storico noleggi</h2>
              <span className="text-xs text-zinc-500">{rentals.length} noleggi</span>
            </div>
            {rentals.length === 0 ? (
              <div className="text-zinc-600 text-sm py-12 text-center border border-dashed border-zinc-800 rounded-sm">
                Nessun noleggio collegato a questo cliente.
              </div>
            ) : (
              <div className="space-y-3">
                {rentals.map((r) => (
                  <div key={r.id} data-testid={`rental-${r.id}`} className="border border-zinc-800 rounded-sm p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium flex items-center gap-2"><Bike className="h-4 w-4 text-[#f98125]" /> Bici {r.bike_numero || r.bike_id}
                        {r.stato && <span className="text-[10px] uppercase tracking-wider text-zinc-500">· {r.stato.replace("_", " ")}</span>}
                      </span>
                      <span className="text-xs text-zinc-500 flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(r.inizio || r.created_at)}</span>
                    </div>
                    {r.checkin?.km_percorsi != null && <p className="text-xs text-[#39FF14] mt-1">{r.checkin.km_percorsi} km percorsi</p>}
                    {r.note && <p className="text-sm text-zinc-400 mt-1">{r.note}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="text-xs text-zinc-600 mt-3">Creato il {fmtDate(cust.created_at)} · Ultima modifica {fmtDate(cust.updated_at)}</div>
        </div>
      </div>

      {form && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-[#141414] border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Contact className="h-5 w-5 text-[#f98125]" /> Modifica cliente</DialogTitle>
            </DialogHeader>
            <CustomerForm form={form} setForm={setForm} />
            <DialogFooter>
              <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
              <button onClick={save} disabled={saving} data-testid="customer-detail-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors disabled:opacity-50">{saving ? "Salvataggio..." : "Salva"}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
